import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CalendarDays, MapPin, Trophy } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { DashboardSection } from './DashboardSection'
import { competitionBadge, trophyBadge } from '../../utils/lifecycle-status'
import type {
  DashboardStaffedPlace,
  DashboardStaffedStage,
  StaffRole,
} from '../../types/dashboard'

function formatDate(value?: string | null) {
  if (!value) return null
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

interface StaffedSectionProps {
  places: DashboardStaffedPlace[]
}

/**
 * The circuits and competitions the viewer works at, until they are over.
 *
 * A judge or a desk volunteer has no entry and follows nothing, so the event
 * they were asked to work was a search away. Here it is one tap: the card goes
 * to the place's page, where the rounds are. A circuit lists its stages still to
 * run, because a role on the circuit holds at every one of them.
 *
 * Nobody on any staff sees no section at all.
 */
export function StaffedSection({ places }: StaffedSectionProps) {
  const { t } = useTranslation()

  if (places.length === 0) return null

  const roleNames = (roles: StaffRole[]) =>
    roles.map((role) => t(`gymSettings.${role}Role`)).join(' · ')

  return (
    <DashboardSection title={t('dashboard.staffed.title')}>
      <div className="flex flex-col gap-3">
        {places.map((place) => {
          const badge = place.kind === 'trophy' ? trophyBadge(place.status) : competitionBadge(place.status)
          const href = place.kind === 'trophy' ? `/trophies/${place.slug}` : `/competitions/${place.slug}`

          return (
            <Card key={`${place.kind}-${place.id}`} data-testid="dashboard-staffed">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center gap-3">
                  <Link to={href} className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar className="size-10 border">
                      <AvatarImage src={place.logo_small_url ?? undefined} alt={place.name} loading="lazy" />
                      <AvatarFallback className="text-xs">{place.name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="min-w-0">
                      <span className="block truncate font-semibold">{place.name}</span>
                      <span className="block truncate text-xs text-muted-foreground" data-testid="staffed-roles">
                        {roleNames(place.roles)}
                      </span>
                      {place.kind === 'competition' && <StageDetails stage={place} trophyName={place.trophy_name} />}
                    </span>
                  </Link>
                  <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>
                </div>

                {place.kind === 'trophy' && place.stages.length > 0 && (
                  <ul className="divide-y">
                    {place.stages.map((stage) => {
                      const stageBadge = competitionBadge(stage.status)
                      return (
                        <li key={stage.id} data-testid="dashboard-staffed-stage">
                          <Link
                            to={`/competitions/${stage.slug}`}
                            className="flex items-center justify-between gap-3 py-2"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium">{stage.name}</span>
                              <StageDetails stage={stage} />
                            </span>
                            <Badge variant={stageBadge.variant}>{t(stageBadge.labelKey)}</Badge>
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </DashboardSection>
  )
}

type StageWhereWhen = Pick<DashboardStaffedStage, 'start_date' | 'venue_name' | 'city'>

function StageDetails({ stage, trophyName }: { stage: StageWhereWhen; trophyName?: string | null }) {
  if (!trophyName && !stage.start_date && !stage.venue_name && !stage.city) return null

  return (
    <span className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
      {trophyName && (
        <span className="flex items-center gap-1">
          <Trophy className="size-3" aria-hidden />
          {trophyName}
        </span>
      )}
      {stage.start_date && (
        <span className="flex items-center gap-1">
          <CalendarDays className="size-3" aria-hidden />
          {formatDate(stage.start_date)}
        </span>
      )}
      {(stage.venue_name || stage.city) && (
        <span className="flex items-center gap-1">
          <MapPin className="size-3" aria-hidden />
          {stage.venue_name ?? stage.city}
        </span>
      )}
    </span>
  )
}
